import type {
  ButtonHTMLAttributes,
  ElementType,
  HTMLAttributes,
  ReactNode,
} from "react";

/* ---------- Logo ---------- */
export const Logo = ({
  size = 20,
  className = "",
}: {
  size?: number;
  className?: string;
}) => (
  <img
    src="/Logo-Arpentia-Export2b-Light.svg"
    alt="Arpentia"
    draggable={false}
    className={"select-none block " + className}
    style={{ height: Math.round(size * 3.0), width: "auto" }}
  />
);

/* ---------- Pill ---------- */
type PillTone = "ink" | "lime" | "soft" | "green" | "red" | "amber";

export const Pill = ({
  children,
  tone = "ink",
  className = "",
  icon,
}: {
  children: ReactNode;
  tone?: PillTone;
  className?: string;
  icon?: ReactNode;
}) => {
  const tones: Record<PillTone, string> = {
    ink: "bg-ink-100 text-ink-700",
    lime: "bg-lime-200 text-ink-900",
    soft: "bg-paper text-ink-700 hairline",
    green: "bg-emerald-100 text-emerald-800",
    red: "bg-rose-100 text-rose-800",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-medium ${tones[tone]} ${className}`}
    >
      {icon && <span className="opacity-80">{icon}</span>}
      {children}
    </span>
  );
};

/* ---------- Button ---------- */
type ButtonVariant = "primary" | "dark" | "ghost" | "soft" | "outline";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
};

export const Button = ({
  children,
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  className = "",
  ...rest
}: ButtonProps) => {
  const sizes: Record<ButtonSize, string> = {
    sm: "h-8 px-3 text-[12.5px]",
    md: "h-10 px-4 text-[13.5px]",
    lg: "h-12 px-5 text-[14.5px]",
  };
  const variants: Record<ButtonVariant, string> = {
    primary:
      "bg-lime-400 text-ink-900 hover:bg-lime-300 shadow-[inset_0_-2px_0_rgba(0,0,0,0.06)]",
    dark: "bg-ink-900 text-white hover:bg-ink-800",
    ghost: "bg-transparent text-ink-900 hover:bg-ink-100",
    soft: "bg-paper text-ink-900 hairline hover:bg-ink-50",
    outline:
      "bg-transparent text-ink-900 hairline-strong hover:bg-ink-50",
  };
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-medium transition ${sizes[size]} ${variants[variant]} disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-auto ${className}`}
    >
      {icon}
      <span>{children}</span>
      {iconRight}
    </button>
  );
};

/* ---------- Card ---------- */
type CardProps = HTMLAttributes<HTMLElement> & {
  children?: ReactNode;
  as?: ElementType;
};

export const Card = ({
  children,
  className = "",
  as: Tag = "div",
  ...rest
}: CardProps) => {
  const Component = Tag as ElementType;
  return (
    <Component
      {...rest}
      className={`bg-paper rounded-xl2 hairline shadow-card ${className}`}
    >
      {children}
    </Component>
  );
};

/* ---------- SectionLabel ---------- */
export const SectionLabel = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={`text-[11px] tracking-[0.14em] uppercase text-ink-500 font-medium ${className}`}
  >
    {children}
  </div>
);

/* ---------- Stat ---------- */
export const Stat = ({
  label,
  value,
  sub,
  icon,
  tone = "paper",
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  tone?: "paper" | "lime";
}) => (
  <Card className={`p-5 ${tone === "lime" ? "bg-lime-200 !shadow-none hairline" : ""}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-[12px] text-ink-600">{label}</div>
        <div className="mt-1.5 text-[30px] font-semibold tabular tracking-tight text-ink-900">
          {value}
        </div>
        {sub && <div className="text-[12px] text-ink-500 mt-1">{sub}</div>}
      </div>
      <div
        className={`w-9 h-9 rounded-full grid place-items-center ${
          tone === "lime"
            ? "bg-ink-900 text-lime-200"
            : "bg-ink-50 text-ink-700 hairline"
        }`}
      >
        {icon}
      </div>
    </div>
  </Card>
);

/* ---------- Sparkline ---------- */
export const Spark = ({
  points = [3, 5, 4, 6, 8, 7, 9, 10, 12],
  w = 92,
  h = 28,
  color = "#14161A",
}: {
  points?: number[];
  w?: number;
  h?: number;
  color?: string;
}) => {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * step;
      const y = h - ((p - min) / (max - min || 1)) * (h - 4) - 2;
      return (i === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
    })
    .join(" ");
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  );
};
